import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ArrowLeft, Download, Search, ScanLine, UserSearch } from 'lucide-react';
import { Button, Card, Chip, Input, Select } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { formatRelative } from '@/lib/format';
import { ApiError } from '@/lib/api-client';
import { useDiagnosticoList, useVarrerTodos } from '@/modules/diagnostico/api';
import { ConsultaAvulsaModal } from '@/modules/diagnostico/components/ConsultaAvulsaModal';
import { DiagnosticoStats } from '@/modules/diagnostico/components/DiagnosticoStats';
import { DiagnosticoTable } from '@/modules/diagnostico/components/DiagnosticoTable';
import { DiagnosticoDetailModal } from '@/modules/diagnostico/components/DiagnosticoDetailModal';
import { VarreduraProgressModal } from '@/modules/diagnostico/components/VarreduraProgressModal';
import { NIVEIS, type Nivel } from '@/modules/diagnostico/schemas';
import { NIVEL_LABEL } from '@/modules/diagnostico/data';

export const Route = createFileRoute('/_authed/diagnostico')({
  component: DiagnosticoPage,
});

function DiagnosticoPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useDiagnosticoList();
  const varrerTodos = useVarrerTodos();

  const [search, setSearch] = useState('');
  const [nivel, setNivel] = useState<Nivel | ''>('');
  const [selectedCpf, setSelectedCpf] = useState<string | null>(null);
  const [varreduraActive, setVarreduraActive] = useState(false);
  const [varreduraTotal, setVarreduraTotal] = useState(0);
  const [consultaAvulsaOpen, setConsultaAvulsaOpen] = useState(false);

  const totalClientes = data?.clientes.length ?? 0;

  function onVarrerTodos() {
    varrerTodos.mutate(undefined, {
      onSuccess: (data) => {
        setVarreduraTotal(data.total ?? totalClientes);
        setVarreduraActive(true);
      },
    });
  }

  function onVarreduraFinished() {
    setVarreduraActive(false);
    void qc.invalidateQueries({ queryKey: ['diagnostico', 'list'] });
  }

  return (
    <PageShell
      eyebrow="Clientes · Diagnóstico Agro"
      title="Diagnóstico de Conformidade"
      lead={
        data?.ultima_varredura
          ? `Última varredura ${formatRelative(data.ultima_varredura)}.`
          : 'Conformidade documental e regulatória da carteira.'
      }
      back={
        <Button
          variant="ghost"
          onClick={() => {
            if (window.history.length > 1) window.history.back();
            else void navigate({ to: '/clientes' });
          }}
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Voltar
        </Button>
      }
      actions={
        <>
          <Button variant="ghost" size="md" onClick={() => setConsultaAvulsaOpen(true)}>
            <UserSearch size={14} strokeWidth={1.5} />
            Consulta avulsa
          </Button>
          <Button variant="secondary" size="md" asChild>
            <a href="/api/v2/diagnostico/export.csv" download>
              <Download size={14} strokeWidth={1.5} />
              Exportar CSV
            </a>
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={onVarrerTodos}
            disabled={varrerTodos.isPending || varreduraActive}
          >
            <ScanLine size={14} strokeWidth={1.5} />
            {varrerTodos.isPending ? 'Disparando…' : 'Rodar varredura'}
          </Button>
        </>
      }
    >
      <Stack gap="lg">
        {data && <DiagnosticoStats data={data} onSelectCpf={setSelectedCpf} />}

        <VarreduraProgressModal
          open={varreduraActive}
          initialTotal={varreduraTotal}
          onClose={() => setVarreduraActive(false)}
          onFinished={onVarreduraFinished}
        />

        {varrerTodos.error && (
          <p className="text-xs text-danger">
            {varrerTodos.error instanceof ApiError
              ? varrerTodos.error.message
              : 'Falha ao disparar varredura'}
          </p>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1 md:max-w-md">
            <Search
              size={14}
              strokeWidth={1.5}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, CPF ou município"
              className="pl-8"
            />
          </div>
          <Select
            value={nivel}
            onChange={(e) => setNivel(e.target.value as Nivel | '')}
            className="md:w-44"
          >
            <option value="">Todos os níveis</option>
            {NIVEIS.map((n) => (
              <option key={n} value={n}>
                {NIVEL_LABEL[n]}
              </option>
            ))}
          </Select>
          <Chip tone="neutral">
            {data?.clientes.length ?? 0} clientes
          </Chip>
        </div>

        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando diagnósticos…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar diagnósticos.'}
            </p>
          </Card>
        ) : totalClientes === 0 ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">
              Nenhum diagnóstico ainda. Rode uma varredura pra começar.
            </p>
          </Card>
        ) : (
          <DiagnosticoTable
            items={data?.clientes ?? []}
            search={search}
            nivelFilter={nivel}
            onSelect={setSelectedCpf}
          />
        )}
      </Stack>

      <DiagnosticoDetailModal cpf={selectedCpf} onClose={() => setSelectedCpf(null)} />
      <ConsultaAvulsaModal
        open={consultaAvulsaOpen}
        onClose={() => setConsultaAvulsaOpen(false)}
      />
    </PageShell>
  );
}
